/**
 * @file RequestTable 请求表格
 * @description 搜索表单 + DataTable + 可选分页；封装列表请求生命周期，也可直接传入本地 data。
 *
 * @whenToUse 后台标准列表页（筛选 → 表格 → 分页）；`request` 来自 `@/api/*`，或用 `data` 喂本地/受控数据。
 * @whenNotToUse 非列表形态的复杂看板。
 */

import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react'
import { ChevronDown, ChevronUp, RotateCcw, Search } from 'lucide-react'
import { ApiError } from '@/lib/client.ts'
import { cn } from '@/lib/cn.ts'
import { Button } from '@/components/ui/button.tsx'
import {
  DataTable,
  type DataTableColumn,
  type DataTableProps,
} from '@/components/ui/data-table.tsx'
import { toast } from '@/components/ui/toast.tsx'
import { SearchFormProvider } from '@/components/ui/search-form-context.tsx'

/** 归一化后的列表结果（组件内部统一） */
export type RequestTableResult<TRow> = {
  /** 当前页（或不分页时的全部）行数据 */
  rows: TRow[]
  /** 总条数；不分页时通常等于 rows.length */
  total: number
}

/** 请求参数：查询字段 + 可选分页（pageNum / pageSize） */
export type RequestTableParams<TQuery extends object> = TQuery & {
  /** 页码，从 1 开始；仅 pagination 开启时由组件注入 */
  pageNum?: number
  /** 每页条数；仅 pagination 开启时由组件注入 */
  pageSize?: number
}

/** 搜索表单渲染上下文 */
export type RequestTableSearchContext<TQuery extends object> = {
  /** 当前表单值（草稿，提交前不触发请求） */
  values: TQuery
  /** 合并更新表单值 */
  setValues: (patch: Partial<TQuery>) => void
  /** 设置单个字段 */
  setField: <K extends keyof TQuery>(key: K, value: TQuery[K]) => void
  /** 提交搜索（重置到第 1 页并请求） */
  submit: () => void
  /** 重置为 initialValues 并请求 */
  reset: () => void
  /** 筛选项是否展开（仅 collapsible 时有意义） */
  expanded: boolean
  /** 展开 / 收起筛选 */
  setExpanded: (expanded: boolean) => void
}

/** 通过 ref 暴露给外部的命令式方法 */
export type RequestTableHandle = {
  /** 按当前筛选与页码重新请求（仅 `request` 模式生效） */
  refresh: () => void
}

export type RequestTableProps<
  TRow extends { id: string },
  TQuery extends object,
  TResponse = RequestTableResult<TRow> | TRow[],
  TParams extends object = RequestTableParams<TQuery>,
> = {
  /**
   * 列表请求（通常直接传入 `@/api` 中的函数）。
   * 与 `data` 二选一；同时传入时以 `request` 为准。
   */
  request?: (params: TParams) => Promise<TResponse>
  /**
   * 外部行数据。无 `request` 时作为数据源；开启分页时在本地切片。
   */
  data?: TRow[]
  /**
   * 本地 `data` 模式的筛选函数；查询 / 重置时按当前 query 过滤后再分页。
   */
  filterData?: (data: TRow[], query: TQuery) => TRow[]
  /**
   * 请求前处理参数（可裁剪 / 改写筛选项、补默认值等）。
   * 返回值将作为最终传给 `request` 的参数。
   */
  beforeRequest?: (params: RequestTableParams<TQuery>) => TParams | Promise<TParams>
  /**
   * 请求后处理响应，归一化为 `{ rows, total }`。
   * 不传时：
   * - 分页：期望 `{ rows, total }`（如 `getPage` 返回值）
   * - 不分页：期望行数组（如 `get` 解包后的 `data`）
   */
  afterRequest?: (response: TResponse) => RequestTableResult<TRow>
  /** 表格列配置（同 DataTable） */
  columns: DataTableColumn<TRow>[]
  /** 每页条数；默认 20；仅 `pagination` 为 true 时生效 */
  pageSize?: number
  /**
   * 是否分页。
   * - true（默认）：请求注入 `pageNum` / `pageSize`，展示底部分页器；`data` 模式则本地切片
   * - false：不注入分页字段、不展示分页器，一次展示全量
   */
  pagination?: boolean
  /** 搜索表单；不传则不渲染顶部筛选区 */
  searchForm?: {
    /** 搜索表单容器类名 */
    className?: string
    /** 初始查询值 */
    initialValues: TQuery
    /** 表单项渲染 */
    items: (ctx: RequestTableSearchContext<TQuery>) => ReactNode
    /** 是否展示默认操作按钮（查询、重置、展开等）；默认 true */
    showActions?: boolean
    /** 是否展示「重置」按钮；默认 true；仅 showActions 为 true 时生效 */
    showReset?: boolean
    /** 是否支持展开 / 收起筛选项；默认 false */
    collapsible?: boolean
    /** 可折叠时默认是否展开；默认 false（收起） */
    defaultExpanded?: boolean
    /** 搜索表单右侧自定义内容（如「新建」按钮）；与筛选项左右分布，换行时仍靠右对齐 */
    extra?: ReactNode
  }
  /** 行选择（透传 DataTable） */
  rowSelection?: DataTableProps<TRow>['rowSelection']
  /** 挂载后是否自动请求；默认 true；仅 `request` 模式生效 */
  autoLoad?: boolean
  /**
   * 外部刷新令牌：值变化时按当前筛选与页码重新请求，不卸载表格、不清空现有行。
   * 仅 `request` 模式生效；首次渲染跳过（由 autoLoad 负责首拉）。
   */
  reloadKey?: number | string
  className?: string
}

/**
 * 将响应归一化为列表结果。
 * @param response 原始响应
 * @param afterRequest 可选转换
 * @param pagination 是否分页模式
 */
function normalizeResult<TRow, TResponse>(
  response: TResponse,
  afterRequest: ((response: TResponse) => RequestTableResult<TRow>) | undefined,
  pagination: boolean,
): RequestTableResult<TRow> {
  if (afterRequest) return afterRequest(response)
  if (pagination) {
    return response as RequestTableResult<TRow>
  }
  const rows = response as TRow[]
  return { rows, total: rows.length }
}

/**
 * 从本地 `data` 按页切片。
 * @param source 全量行
 * @param page 页码（从 1 起）
 * @param pageSize 每页条数
 * @param pagination 是否分页
 */
function sliceLocalData<TRow>(
  source: TRow[],
  page: number,
  pageSize: number,
  pagination: boolean,
): RequestTableResult<TRow> {
  if (!pagination) {
    return { rows: source, total: source.length }
  }
  const start = (page - 1) * pageSize
  return {
    rows: source.slice(start, start + pageSize),
    total: source.length,
  }
}

/**
 * 请求或本地数据驱动的列表：搜索表单 + DataTable + 可选分页。
 */
export function RequestTable<
  TRow extends { id: string },
  TQuery extends object,
  TResponse = RequestTableResult<TRow> | TRow[],
  TParams extends object = RequestTableParams<TQuery>,
>({
  request,
  data,
  filterData,
  beforeRequest,
  afterRequest,
  columns,
  pageSize = 20,
  pagination = true,
  searchForm,
  rowSelection,
  autoLoad = true,
  reloadKey,
  className,
  ref,
}: RequestTableProps<TRow, TQuery, TResponse, TParams> & {
  ref?: Ref<RequestTableHandle>
}) {
  const initialValues = searchForm?.initialValues ?? ({} as TQuery)
  const collapsible = searchForm?.collapsible === true
  const [draft, setDraft] = useState<TQuery>(initialValues)
  const [query, setQuery] = useState<TQuery>(initialValues)
  const [page, setPage] = useState(1)
  const [list, setList] = useState<TRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(
    searchForm?.defaultExpanded === true,
  )
  const requestSeq = useRef(0)
  /** 跳过 reloadKey 的首次 effect，避免与 autoLoad 重复请求 */
  const reloadKeyReady = useRef(false)

  const setValues = (patch: Partial<TQuery>) => {
    setDraft((prev) => ({ ...prev, ...patch }))
  }

  const setField = <K extends keyof TQuery>(key: K, value: TQuery[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  /**
   * 解析本地 data（可选筛选）
   * @param nextQuery 已提交查询
   */
  const resolveLocalSource = (nextQuery: TQuery) => {
    const source = data ?? []
    return filterData ? filterData(source, nextQuery) : source
  }

  const applyLocalData = (source: TRow[], nextPage: number) => {
    if (pagination) {
      const maxPage = Math.max(1, Math.ceil(source.length / pageSize) || 1)
      const safePage = Math.min(nextPage, maxPage)
      if (safePage !== nextPage) {
        setPage(safePage)
        return
      }
    }
    const result = sliceLocalData(source, nextPage, pageSize, pagination)
    setList(result.rows)
    setTotal(result.total)
  }

  const fetchList = async (nextQuery: TQuery, nextPage: number) => {
    if (!request) return
    const seq = ++requestSeq.current
    setLoading(true)
    try {
      let baseParams: RequestTableParams<TQuery> = { ...nextQuery }
      if (pagination) {
        baseParams = { ...baseParams, pageNum: nextPage, pageSize }
      }
      const params: TParams = beforeRequest
        ? await beforeRequest(baseParams)
        : // 默认 TParams 即 RequestTableParams<TQuery>；beforeRequest 缺省时直接透传
          (baseParams as unknown as TParams)
      const response = await request(params)
      if (seq !== requestSeq.current) return
      const result = normalizeResult(response, afterRequest, pagination)
      setList(result.rows)
      setTotal(pagination ? result.total : result.rows.length)
    } catch (error) {
      if (seq !== requestSeq.current) return
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : '加载失败'
      toast.danger(message)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }

  useImperativeHandle(
    ref,
    () => ({ refresh: () => void fetchList(query, page) }),
    // fetchList 闭包依赖当前 query / page；变化时重建句柄
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 见上
    [query, page, request],
  )

  useEffect(() => {
    if (!request) return
    if (!autoLoad) return
    void fetchList(initialValues, 1)
    // 仅挂载时按 autoLoad 拉一次；后续由查询 / 重置 / 翻页显式触发
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 见上
  }, [])

  useEffect(() => {
    if (!request) return
    if (reloadKey === undefined) return
    if (!reloadKeyReady.current) {
      reloadKeyReady.current = true
      return
    }
    void fetchList(query, page)
    // reloadKey 变化时静默重拉；保留当前筛选与页码
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 见上
  }, [reloadKey])

  useEffect(() => {
    if (request) return
    applyLocalData(resolveLocalSource(query), page)
    // data / query / 分页配置变化时同步本地切片
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 见上
  }, [data, query, page, pageSize, pagination, request, filterData])

  const submit = () => {
    setQuery(draft)
    setPage(1)
    if (request) {
      void fetchList(draft, 1)
      return
    }
    applyLocalData(resolveLocalSource(draft), 1)
  }

  const reset = () => {
    setDraft(initialValues)
    setQuery(initialValues)
    setPage(1)
    if (request) {
      void fetchList(initialValues, 1)
      return
    }
    applyLocalData(resolveLocalSource(initialValues), 1)
  }

  const onPageChange = (nextPage: number) => {
    setPage(nextPage)
    if (request) {
      void fetchList(query, nextPage)
      return
    }
    applyLocalData(resolveLocalSource(query), nextPage)
  }

  const searchCtx: RequestTableSearchContext<TQuery> = {
    values: draft,
    setValues,
    setField,
    submit,
    reset,
    expanded,
    setExpanded,
  }

  const showActions = searchForm?.showActions !== false
  const showReset = searchForm?.showReset !== false
  const searchExtra = searchForm?.extra

  return (
    <div className={cn('space-y-3 flex flex-col flex-1 overflow-hidden', className)}>
      {searchForm ? (
        <SearchFormProvider>
          <form
            className={cn('rounded-xl border border-border p-3 bg-sidebar', searchForm.className)}
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <div className="flex w-full flex-wrap items-end gap-3">
              <div className="flex min-w-0 flex-1 flex-wrap items-end gap-3">
                {searchForm.items(searchCtx)}
                {showActions ? (
                  <div className="flex items-center gap-2">
                    <Button
                      type="submit"
                      icon={<Search className="size-4" aria-hidden />}
                    >
                      查询
                    </Button>
                    {showReset ? (
                      <Button
                        type="button"
                        variant="secondary"
                        icon={<RotateCcw className="size-4" aria-hidden />}
                        onClick={reset}
                      >
                        重置
                      </Button>
                    ) : null}
                    {collapsible ? (
                      <Button
                        type="button"
                        variant="ghost"
                        icon={
                          expanded ? (
                            <ChevronUp className="size-4" aria-hidden />
                          ) : (
                            <ChevronDown className="size-4" aria-hidden />
                          )
                        }
                        onClick={() => setExpanded(!expanded)}
                      >
                        {expanded ? '收起' : '展开'}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {searchExtra ? (
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  {searchExtra}
                </div>
              ) : null}
            </div>
          </form>
        </SearchFormProvider>
      ) : null}

      <DataTable
        columns={columns}
        data={list}
        loading={loading}
        rowSelection={rowSelection}
        pagination={
          pagination
            ? {
                page,
                pageSize,
                total,
                onChange: onPageChange,
              }
            : undefined
        }
      />
    </div>
  )
}
